import type { IAnalyticsProvider, TrackResult, IdentifyResult, UserTraits, GroupTraits } from '../interfaces/IAnalyticsProvider';

// Environment helpers (matches pattern from BaseEmailService / loggerService)
export const APP_ENV = process.env.APP_ENV || process.env.NODE_ENV || 'development';
export const isProduction = APP_ENV === 'production';
export const isStaging = APP_ENV === 'staging';
export const isDevelopment = APP_ENV === 'development';

// Force analytics in development (for testing real provider flow)
// Set ANALYTICS_FORCE_SEND=true in .env to enable
export const forceAnalyticsSend = process.env.ANALYTICS_FORCE_SEND === 'true';

/**
 * Shared analytics configuration derived from environment.
 */
export interface BaseAnalyticsConfig {
    /** Application identifier (admin, management, ordering, pos, etc.) */
    app: string;
    /** Environment (development, staging, production) */
    environment: string;
    /** Default tags applied to all events */
    defaultTags?: Record<string, string>;
}

export function getAnalyticsConfig(app: string): BaseAnalyticsConfig {
    return {
        app,
        environment: APP_ENV,
        defaultTags: {
            app,
            environment: APP_ENV,
        },
    };
}

/**
 * Abstract base analytics provider with all environment and shared logic.
 *
 * Providers extend this and implement only:
 * - `isProviderConfigured()` - whether the provider SDK is ready
 * - `trackRaw(event, properties)` - the actual provider-specific track call
 * - `identifyRaw(userId, traits)` - the actual provider-specific identify call
 * - `pageRaw(name, properties)` - the actual provider-specific page call
 * - `groupRaw(groupId, groupType, traits)` - the actual provider-specific group call
 * - `resetRaw()` - the actual provider-specific reset call
 * - `flushRaw()` - the actual provider-specific flush call
 *
 * All dev mocking, tag merging, environment handling, and error
 * swallowing live here. Mirrors BaseEmailService exactly.
 */
export abstract class BaseAnalyticsProvider implements IAnalyticsProvider {
    protected config: BaseAnalyticsConfig;

    constructor(app: string) {
        this.config = getAnalyticsConfig(app);
    }

    abstract readonly providerName: string;

    isConfigured(): boolean {
        return this.isProviderConfigured();
    }

    track(event: string, properties?: Record<string, unknown>): TrackResult {
        // DEVELOPMENT: Mock events unless ANALYTICS_FORCE_SEND=true
        if (isDevelopment && !forceAnalyticsSend) {
            return { success: true, eventId: `dev-mock-${Date.now()}` };
        }

        if (!this.isProviderConfigured()) {
            return { success: false, error: `${this.providerName} is not configured` };
        }

        try {
            const enriched = this.enrichProperties(properties);
            return this.trackRaw(event, enriched);
        } catch (err) {
            // Analytics must NEVER throw — swallow and return error result
            return { success: false, error: err instanceof Error ? err.message : String(err) };
        }
    }

    identify(userId: string, traits?: UserTraits): IdentifyResult {
        if (isDevelopment && !forceAnalyticsSend) {
            return { success: true };
        }

        if (!this.isProviderConfigured()) {
            return { success: false, error: `${this.providerName} is not configured` };
        }

        try {
            return this.identifyRaw(userId, traits);
        } catch (err) {
            return { success: false, error: err instanceof Error ? err.message : String(err) };
        }
    }

    page(name: string, properties?: Record<string, unknown>): TrackResult {
        if (isDevelopment && !forceAnalyticsSend) {
            return { success: true, eventId: `dev-mock-${Date.now()}` };
        }

        if (!this.isProviderConfigured()) {
            return { success: false, error: `${this.providerName} is not configured` };
        }

        try {
            const enriched = this.enrichProperties(properties);
            return this.pageRaw(name, enriched);
        } catch (err) {
            return { success: false, error: err instanceof Error ? err.message : String(err) };
        }
    }

    group(groupId: string, groupType: string, traits?: GroupTraits): void {
        if (isDevelopment && !forceAnalyticsSend) return;
        if (!this.isProviderConfigured()) return;

        try {
            this.groupRaw(groupId, groupType, traits);
        } catch (_) {
            // Swallow — analytics must never throw
        }
    }

    reset(): void {
        if (!this.isProviderConfigured()) return;

        try {
            this.resetRaw();
        } catch (_) {
            // Swallow
        }
    }

    async flush(): Promise<void> {
        if (!this.isProviderConfigured()) return;

        try {
            await this.flushRaw();
        } catch (_) {
            // Swallow
        }
    }

    setOptOut(_optOut: boolean): void {
        // Default no-op — override in providers that support opt-out
    }

    // ─── Subclass responsibilities ─────────────────────────────────

    /** Whether the provider SDK is initialized and ready */
    protected abstract isProviderConfigured(): boolean;

    /** Send event to the actual provider */
    protected abstract trackRaw(event: string, properties: Record<string, unknown>): TrackResult;

    /** Identify user with the actual provider */
    protected abstract identifyRaw(userId: string, traits?: UserTraits): IdentifyResult;

    /** Track page view with the actual provider */
    protected abstract pageRaw(name: string, properties: Record<string, unknown>): TrackResult;

    /** Associate user with group in the actual provider */
    protected abstract groupRaw(groupId: string, groupType: string, traits?: GroupTraits): void;

    /** Reset user identity in the actual provider */
    protected abstract resetRaw(): void;

    /** Flush pending events in the actual provider */
    protected abstract flushRaw(): Promise<void>;

    // ─── Shared helpers ────────────────────────────────────────────

    /** Merge default tags + config into event properties */
    protected enrichProperties(properties?: Record<string, unknown>): Record<string, unknown> {
        return {
            ...this.config.defaultTags,
            ...properties,
            _app: this.config.app,
            _environment: this.config.environment,
            _provider: this.providerName,
            _timestamp: new Date().toISOString(),
        };
    }
}
